import React from 'react';
import { Label } from '@rocco/components/label';
import {
    DOCUMENT_ROLE,
    type DocumentRole,
    type DocumentCollaborator,
    type DocumentCollaboratorSelection,
} from '../../types';
import { DocumentCollaboratorPicker } from './DocumentCollaboratorPicker';

/**
 * Props for the DocumentAccessControl component.
 */
interface DocumentAccessControlProps {
    /** The ID of the document */
    documentId?: string;
    /** The ID of the BuildHub project (optional for new documents) */
    buildHubId?: string;
    /** All BuildHub collaborators to show in restricted mode */
    buildHubCollaborators?: DocumentCollaborator[];
    /** Currently selected collaborators with access (for restricted mode) */
    selectedCollaborators?: DocumentCollaboratorSelection[];
    /** Callback when collaborators change */
    onCollaboratorsChange?: (
        collaborators: DocumentCollaboratorSelection[]
    ) => void;
    /** Whether the control is disabled */
    disabled?: boolean;
    /** Email of the document owner to filter from collaborator picker */
    ownerEmail?: string;
    /** Default access level for future team members */
    futureTeamMemberRole?: DocumentRole | 'no-access';
    /** Callback when future team member default role changes */
    onFutureTeamMemberRoleChange?: (role: DocumentRole | 'no-access') => void;
    /** Link access role (for "Anyone with the link") */
    linkAccessRole?: DocumentRole | 'no-access';
    /** Callback when link access role changes */
    onLinkAccessRoleChange?: (role: DocumentRole | 'no-access') => void;
}

/**
 * Main access control panel for BuildHub documents.
 * Provides Google Docs-style access control with inheritance, project-wide,
 * restricted, and link-based access modes.
 */
export const DocumentAccessControl: React.FC<DocumentAccessControlProps> = ({
    documentId,
    buildHubId,
    buildHubCollaborators = [],
    selectedCollaborators = [],
    onCollaboratorsChange,
    disabled = false,
    ownerEmail,
    futureTeamMemberRole = DOCUMENT_ROLE.Commenter,
    onFutureTeamMemberRoleChange,
    linkAccessRole = 'no-access',
    onLinkAccessRoleChange,
}) => {
    return (
        <div className="space-y-2">
            <Label className="text-base font-medium">People with access</Label>
            <DocumentCollaboratorPicker
                buildHubId={buildHubId}
                documentId={documentId}
                buildHubCollaborators={buildHubCollaborators}
                selectedCollaborators={selectedCollaborators}
                onCollaboratorsChange={onCollaboratorsChange}
                disabled={disabled}
                ownerEmail={ownerEmail}
                futureTeamMemberRole={futureTeamMemberRole}
                onFutureTeamMemberRoleChange={onFutureTeamMemberRoleChange}
                linkAccessRole={linkAccessRole}
                onLinkAccessRoleChange={onLinkAccessRoleChange}
            />
        </div>
    );
};
